import React from 'react';
import { Typography, Box, Chip } from '@mui/material';
import { parseSegmentTime } from '../../../../../lib/transcriptionHelpers';
import { onClickTimestamp } from '../../../../../lib/commonUtils';

interface Props {
  text: string;
}

const Description: React.FC<Props> = ({ text }) => {
  const formatTimestamp = (timestamp: string) => {
    const cleanTimestamp = timestamp.replace(/[[\]()\s]/g, '');
    const timeParts = cleanTimestamp.split(':').map(Number);

    if (timeParts.length === 2) {
      const [minutes, seconds] = timeParts;
      return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(
        2,
        '0'
      )}`;
    }
    if (timeParts.length === 3) {
      const [hours, minutes, seconds] = timeParts;
      return hours === 0
        ? `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(
            2,
            '0'
          )}`
        : `${String(hours).padStart(2, '0')}:${String(minutes).padStart(
            2,
            '0'
          )}:${String(seconds).padStart(2, '0')}`;
    }

    return timestamp;
  };

  const convertTimestampsToLinks = (line: string) => {
    const timestampRegex = /[[(]?\s*(?:(?:\d+:)?\d{2}|\d):\d{2}\s*[)\]]?/g; // catch [ 00:00 ], ( 00:00 ), 00:00, [ 00:00:00 ], ( 00:00:00 ), 00:00:00, even without the leading 0 for h and m
    const parts = line.split(timestampRegex);
    const matches = line.match(timestampRegex);

    return parts.flatMap((part, partIndex) => {
      if (matches && partIndex < matches.length) {
        const match = matches[partIndex];
        const cleanMatch = match.replace(/[[\]()\s]/g, '');
        const time = parseSegmentTime(`[${cleanMatch}]`); // parse time in the format [00:00] or [00:00:00]
        const displayTimestamp = formatTimestamp(match);

        const handleClick = () => {
          onClickTimestamp(time);
        };

        const chipWidth = displayTimestamp.length * 10; // Approximate width based on character count

        return [
          <Typography
            variant="body1"
            component="span"
            key={`part-${partIndex}`}
            sx={{ display: 'inline', verticalAlign: 'middle' }}
          >
            {part}
          </Typography>,
          <Chip
            key={partIndex}
            label={displayTimestamp}
            onClick={handleClick}
            clickable
            sx={{
              cursor: 'pointer',
              backgroundColor: 'unset',
              color: 'primary.main',
              width: `${chipWidth}px`,
              '&:hover': {
                backgroundColor: 'background.default',
              },
              '& .MuiChip-label': {
                padding: '0 4px',
                color: 'primary.main',
                fontSize: '0.875rem',
                width: '100%',
                textAlign: 'center',
              },
              verticalAlign: 'middle',
            }}
          />,
        ];
      }
      return (
        <Typography variant="body1" component="span" key={`part-${partIndex}`}>
          {part}
        </Typography>
      );
    });
  };

  const renderContentWithLineBreaks = (content: string) => {
    const lines = content.split(/\r?\n/);
    return lines.map((line, index) => (
      <Typography variant="body1" key={index}>
        {convertTimestampsToLinks(line)}
        {index < lines.length - 1 && <br />}
      </Typography>
    ));
  };

  return (
    <Box sx={{ whiteSpace: 'pre-line' }}>
      {renderContentWithLineBreaks(text)}
    </Box>
  );
};

export default Description;
